// frontend/src/features/portfolio-diversification-optimizer-pro/api.ts
//
// Async ARQ job polling + debounced symbol autocomplete for the PROTECTED
// Portfolio Diversification Optimizer PRO dashboard.
//
// This is a paid-tier tool, so every call goes through the shared authenticated
// ``apiClient`` (Bearer token, silent 401 refresh, cross-site cookie) rather
// than a bare ``fetch`` -- that keeps the auth + error contract identical to
// every other internal feature. ``apiClient``'s baseURL already carries the
// ``/api/v1`` prefix, so endpoint paths here are relative to the domain router.
//
// Flow:
//   1. POST /analyze enqueues the ARQ job and returns {job_id, status}. On a
//      warm Redis semantic-cache hit the backend returns status "complete"
//      immediately, so we fetch /results/{job_id} without a polling delay.
//   2. Otherwise we poll /results/{job_id} every 650ms until the job is
//      "complete" or "failed", aborting in-flight requests when the user
//      submits a new portfolio or unmounts.

import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import { apiClient } from "../../api/client";
import type {
  AnalyzeProAccepted,
  CoverageResponse,
  DiversificationProJobStatus,
  DiversificationProResult,
  JobState,
  PortfolioProRequest,
  SymbolSuggestion,
  SymbolSuggestResponse,
} from "./types";

const BASE = "/portfolio-diversification-optimizer-pro";
const POLL_INTERVAL_MS = 650;
const MAX_POLL_ATTEMPTS = 90; // ~60 seconds timeout safety

// Translate an axios failure into a clean, user-facing Error. Mirrors the
// backend envelope from app/core/exceptions.py: { status, message, data } as
// well as FastAPI's raw { detail } (string or validation-array) shapes.
function toError(error: unknown, fallback: string): Error {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as
      | { detail?: unknown; message?: unknown; data?: unknown }
      | undefined;
    const fieldErrors = Array.isArray(body?.data) ? body?.data : body?.detail;
    if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
      const first = fieldErrors[0] as { msg?: string };
      if (first?.msg) return new Error(first.msg.replace(/^Value error, /, ""));
    }
    if (typeof body?.detail === "string") return new Error(body.detail);
    if (typeof body?.message === "string" && error.response?.status !== 500) {
      return new Error(body.message);
    }
    return new Error(fallback);
  }
  return error instanceof Error ? error : new Error(fallback);
}

export async function enqueueProAnalysis(
  payload: PortfolioProRequest,
  signal?: AbortSignal
): Promise<AnalyzeProAccepted> {
  try {
    const { data } = await apiClient.post<AnalyzeProAccepted>(
      `${BASE}/analyze`,
      payload,
      { signal }
    );
    return data;
  } catch (error) {
    throw toError(error, "Analysis request failed. Please try again.");
  }
}

export async function pollProResults(
  jobId: string,
  signal?: AbortSignal
): Promise<DiversificationProJobStatus> {
  try {
    const { data } = await apiClient.get<DiversificationProJobStatus>(
      `${BASE}/results/${encodeURIComponent(jobId)}`,
      { signal }
    );
    return data;
  } catch (error) {
    throw toError(error, "Failed to poll the analysis status.");
  }
}

export async function fetchSymbolSuggestions(
  query: string,
  limit = 8,
  signal?: AbortSignal
): Promise<SymbolSuggestion[]> {
  const trimmed = query.trim();
  if (!trimmed) return [];
  try {
    const { data } = await apiClient.get<SymbolSuggestResponse>(`${BASE}/symbols`, {
      params: { q: trimmed, limit },
      signal,
    });
    return data.results ?? [];
  } catch {
    // Typeahead degrades silently -- a failed suggestion fetch must never
    // break the form.
    return [];
  }
}

export async function fetchProCoverage(tickers: string[]): Promise<CoverageResponse> {
  const { data } = await apiClient.post<CoverageResponse>(`${BASE}/coverage`, { tickers });
  return data;
}

// ---------------------------------------------------------------------------
// React Hook: useDiversificationProCoverage
// ---------------------------------------------------------------------------
// Probes how much overlapping price history the local store holds for the
// current holdings, so the builder only offers lookback windows we can serve.
export const useDiversificationProCoverage = (tickers: string[]) =>
  useQuery<CoverageResponse>({
    queryKey: ["diversification-pro-coverage", [...tickers].sort().join(",")],
    queryFn: () => fetchProCoverage(tickers),
    enabled: tickers.length > 0,
    staleTime: 5 * 60_000,
    retry: false,
  });

// ---------------------------------------------------------------------------
// React Hook: useDiversificationProAnalysis
// ---------------------------------------------------------------------------
export interface UseDiversificationProReturn {
  runAnalysis: (req: PortfolioProRequest) => Promise<void>;
  result: DiversificationProResult | null;
  status: JobState | "idle";
  isLoading: boolean;
  error: string | null;
  jobId: string | null;
}

export function useDiversificationProAnalysis(): UseDiversificationProReturn {
  const [result, setResult] = useState<DiversificationProResult | null>(null);
  const [status, setStatus] = useState<JobState | "idle">("idle");
  const [error, setError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const runAnalysis = useCallback(async (req: PortfolioProRequest) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setError(null);
    setResult(null);
    setStatus("queued");

    try {
      const accepted = await enqueueProAnalysis(req, controller.signal);
      setJobId(accepted.job_id);
      setStatus(accepted.status);

      let attempts = 0;
      while (!controller.signal.aborted && attempts < MAX_POLL_ATTEMPTS) {
        // Skip the initial delay only when the first POST already came back
        // "complete" (warm semantic cache) -- then fetch the payload at once.
        if (attempts > 0 || accepted.status !== "complete") {
          await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        }
        if (controller.signal.aborted) return;

        const polled = await pollProResults(accepted.job_id, controller.signal);
        setStatus(polled.status);

        if (polled.status === "complete" && polled.data) {
          setResult(polled.data);
          return;
        }
        if (polled.status === "failed" || polled.status === "not_found") {
          setError(polled.error || "The analysis job could not be completed.");
          return;
        }
        attempts += 1;
      }

      if (!controller.signal.aborted) {
        setStatus("failed");
        setError("Analysis timed out waiting for the worker. Please retry.");
      }
    } catch (err: unknown) {
      // Swallow user-initiated cancellations (new submit / unmount).
      if (axios.isCancel(err) || (err instanceof DOMException && err.name === "AbortError")) {
        return;
      }
      setStatus("failed");
      setError(err instanceof Error ? err.message : "Unexpected network error.");
    }
  }, []);

  const isLoading = status === "queued" || status === "in_progress";

  return { runAnalysis, result, status, isLoading, error, jobId };
}

// ---------------------------------------------------------------------------
// React Hook: useSymbolAutocomplete (Debounced)
// ---------------------------------------------------------------------------
export function useSymbolAutocomplete(query: string, debounceMs = 180) {
  const [suggestions, setSuggestions] = useState<SymbolSuggestion[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 1) {
      setSuggestions([]);
      setIsSearching(false);
      return;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const items = await fetchSymbolSuggestions(trimmed, 8, controller.signal);
        if (!controller.signal.aborted) {
          setSuggestions(items);
        }
      } catch {
        if (!controller.signal.aborted) {
          setSuggestions([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsSearching(false);
        }
      }
    }, debounceMs);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query, debounceMs]);

  return { suggestions, isSearching };
}
